import React, { useContext, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import _ from 'underscore';
import { Stack } from '@bingads-webui-react/fabric-control';
import { ResponsiveAd } from '@bingads-webui/campaign-odata-schemas';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { mergeI18n } from '@bingads-webui-campaign-react/i18n-merger';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { ValidationGroup } from '@bingads-webui-react/validation';
import { ThirdPartyMeasurementContainerWithValidation } from '@bingads-webui-campaign-react/third-party-measurement';
import { CopilotRepositoryProvider } from '@bingads-webui/inline-copilot';
import { withRecommendationContext } from '@bingads-webui-campaign-react/perf-max-recommendation';
import { AdvancedSettings } from '@bingads-webui-campaign-react/display-plus-native-ads';
import { setClarityTag } from '@bingads-webui-universal/clarity';
import { CampaignFetchProvider } from '@bingads-webui-campaign-react/campaign-http';

import { ValidatedVideoExperience } from './validated-video-experience';
import { getAdsStepStyles } from './styles';
import { IconButton } from '../common/shared-components';
import { FinalUrl } from './final-url';
import { useVideoAdsCopilot } from './use-video-ads-copilot';
import { getODataConfig } from '../model/ad-bulk';

const VideoPanel = ({
  i18n,
  perfMarker,
  cid,
  aid,
  onUpdate,
  onRemovePanel,
  removeCloseButton,
  permissions,
  videoAd,
  keyIndex,
  isSelectedPanel,
  videoRequirementForDeal,
  oDataConfig: oDataConfigProp,
  customStyleFunction,
  useCustomStyleFunction,
  biddingScheme,
  isForDeal,
  isXandrServingCampaign,
  dealLoaded,
  setCopilotFinalUrl,
  isAdGridEditorMode,
  copilotFetchTaskManager,
  copilotEnabled,
  loadClipchampPlayer,
  useBrandKit,
  setUseBrandKit,
}) => {
  const defaultClasses = useDefaultStyles(getAdsStepStyles);

  const customClasses = useDefaultStyles(customStyleFunction);

  const classes = useCustomStyleFunction ? customClasses : defaultClasses;

  const videoTypesWithOrder = ['VideoMedia16X9'];

  const serverContext = useContext(ServerContext);

  const {
    DynamicPermissions: {
      EnableMSANVideoCampaigns,
    },
  } = serverContext;

  const oDataConfig = oDataConfigProp || getODataConfig(serverContext);

  const isBiddingStrategyCPV = _.get(biddingScheme, 'Type') === 'ManualCpv';

  const legacyI18n = useMemo(() => window.globalLegacyI18n.get('$/i18n!legacy/global'), []);

  const i18nMerged = useMemo(() => mergeI18n(legacyI18n, i18n), [legacyI18n, i18n]);

  const finalUrlsObj = { FinalUrls: videoAd && videoAd.FinalUrls };

  const { fetch, isBrandKitEmpty, fetchBrandKit } = useVideoAdsCopilot();

  const verifiedTrackingSettingObj = useMemo(() => ({
    VerifiedTrackingSettings: videoAd && videoAd.VerifiedTrackingSettings,
  }), [videoAd]);

  const shouldRepairGoogleImportVideoAd = permissions && permissions.IsGoogleImportVideoAdsEnabled
    && _.get(videoAd, 'video.sourceType') === 'GoogleImported'
    && _.get(videoAd, 'video.status') === 'Failed';

  const onValidationUpdate = (result) => {
    onUpdate(result);
  };

  useEffect(() => {
    fetchBrandKit();
  }, [fetchBrandKit]);

  useEffect(() => {
    if (copilotEnabled) {
      fetch(copilotFetchTaskManager, useBrandKit);
      setClarityTag({ VideoCopilotGeneration: true });
    }
  }, [copilotEnabled, copilotFetchTaskManager, fetch, useBrandKit]);

  const advancedSettings = useMemo(() => (
    <AdvancedSettings
      trackingUrlTemplate={videoAd && videoAd.TrackingUrlTemplate}
      finalUrlSuffix={videoAd && videoAd.FinalUrlSuffix}
      urlCustomParameters={videoAd && videoAd.UrlCustomParameters}
      verifiedTrackingSettings={verifiedTrackingSettingObj.VerifiedTrackingSettings}
      impressionTrackingUrls={videoAd && videoAd.ImpressionTrackingUrls}
      biddingScheme={_.get(biddingScheme, 'Type')}
      isDisplay={false}
      isVideo
    />
  ), [biddingScheme, verifiedTrackingSettingObj.VerifiedTrackingSettings, videoAd]);

  return (
    <div className={classNames([shouldRepairGoogleImportVideoAd ? classes.videoRepairPanelRoot : classes.videoPanelRoot, isSelectedPanel ? classes.selectedPanel : ''])}>
      <ValidationGroup
        validationProps={{
          schema: ResponsiveAd,
          onValidate: onValidationUpdate,
        }}
        key={`${keyIndex}-validationGroup`}
      >
        <Stack tokens={{ childrenGap: 16 }}>
          {!removeCloseButton &&
            <div className={classes.videoPanelHeader}>
              <IconButton iconProps={{ iconName: 'Cancel' }} title="Remove ad" className="remove-ad-panel" onClick={onRemovePanel} />
            </div>
          }
          <FinalUrl
            containerClassName={classes.videoPanelFinalUrlContainer}
            i18n={i18n}
            finalUrlsObj={finalUrlsObj}
            setCopilotFinalUrl={setCopilotFinalUrl}
            validationProps={{
              field: 'FinalUrls',
            }}
            isAdGridEditorMode={isAdGridEditorMode}
          />
          <div className={classes.videoPanelContent}>
            <ValidatedVideoExperience
              i18n={i18nMerged}
              perfMarker={perfMarker}
              permissions={permissions}
              oDataConfig={oDataConfig}
              videoRequirementForDeal={videoRequirementForDeal}
              cid={cid}
              aid={aid}
              allowedVideoSizes={videoTypesWithOrder}
              disableRemoveAll
              hideRatioLabel
              videoAd={videoAd}
              key={`${keyIndex}-videoExperience`}
              idx={keyIndex}
              videoExperienceCircleUploadType
              disableUploadTextField
              videoPickerContainerHeight="auto"
              serverContext={serverContext}
              copilotEnabled={copilotEnabled}
              isCTV={isBiddingStrategyCPV}
              loadClipchampPlayer={loadClipchampPlayer}
              useBrandKit={useBrandKit}
              setUseBrandKit={setUseBrandKit}
              isBrandKitEmpty={isBrandKitEmpty}
            />
          </div>
          {
            // If Xandr serving campaign
            // and
            // CPM and enable IAS
            isXandrServingCampaign &&
            permissions.EnableIASVerifiedImpressionTrackingURLsForBrandAwarenessCampaign && !isBiddingStrategyCPV
            && (
              <ThirdPartyMeasurementContainerWithValidation
                i18n={i18n}
                videoAd={videoAd}
                validationProps={{
                  data: verifiedTrackingSettingObj,
                  initialValue: verifiedTrackingSettingObj,
                }}
                dealLoaded={dealLoaded}
                isForDeal={isForDeal}
                isMOATEnabled={permissions.IsMOATEnabled}
              />
            )}
          {
            // Hide when Deal or CTV or EditMode Xandr Serving
            EnableMSANVideoCampaigns &&
            !isForDeal && !isBiddingStrategyCPV && !(isAdGridEditorMode && isXandrServingCampaign) &&
            <div>
              {advancedSettings}
            </div>
          }
        </Stack>
      </ValidationGroup>
    </div>
  );
};

VideoPanel.propTypes = {
  perfMarker: PropTypes.shape({}).isRequired,
  permissions: PropTypes.shape({
    EnableIASVerifiedImpressionTrackingURLsForBrandAwarenessCampaign: PropTypes.bool,
    IsMOATEnabled: PropTypes.bool,
    IsGoogleImportVideoAdsEnabled: PropTypes.bool,
    dynamic: PropTypes.shape({
      VideoCopilotGeneration: PropTypes.bool,
    }),
  }).isRequired,
  cid: PropTypes.number.isRequired,
  aid: PropTypes.number.isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
    getLocale: PropTypes.func.isRequired,
  }).isRequired,
  biddingScheme: PropTypes.shape({}).isRequired,
  onUpdate: PropTypes.func.isRequired,
  onRemovePanel: PropTypes.func.isRequired,
  removeCloseButton: PropTypes.bool.isRequired,
  videoAd: PropTypes.shape([]).isRequired,
  keyIndex: PropTypes.number.isRequired,
  isSelectedPanel: PropTypes.bool.isRequired,
  oDataConfig: PropTypes.shape({}),
  customStyleFunction: PropTypes.func,
  useCustomStyleFunction: PropTypes.bool,
  videoRequirementForDeal: PropTypes.number,
  isForDeal: PropTypes.bool,
  dealLoaded: PropTypes.bool,
  setCopilotFinalUrl: PropTypes.func.isRequired,
  isAdGridEditorMode: PropTypes.bool,
  copilotFetchTaskManager: PropTypes.shape({}).isRequired,
  copilotEnabled: PropTypes.bool.isRequired,
  isXandrServingCampaign: PropTypes.bool,
};

VideoPanel.defaultProps = {
  oDataConfig: null,
  customStyleFunction: _.noop,
  videoRequirementForDeal: null,
  useCustomStyleFunction: false,
  isForDeal: false,
  dealLoaded: null,
  isAdGridEditorMode: false,
  isXandrServingCampaign: false,
};

const VideoPanelWithContexts = ({ ...props }) => {
  const [copilotFinalUrl, setCopilotFinalUrl] = React.useState('');
  const serverContext = useContext(ServerContext);
  const { permissions } = props;
  const isUsingVideoTemplates = permissions && permissions.dynamic && permissions.dynamic.VideoTemplateUI;

  return (
    <CampaignFetchProvider>
      <CopilotRepositoryProvider
        campaignId="-1"
        finalUrl={copilotFinalUrl}
        locale={props.i18n.getLocale()}
        recommendationType="Video"
        serverContext={serverContext}
        enableLogoAndCTA={isUsingVideoTemplates}
        useClipchamp={isUsingVideoTemplates}
      >
        <VideoPanel {...props} setCopilotFinalUrl={setCopilotFinalUrl} />
      </CopilotRepositoryProvider>
    </CampaignFetchProvider>);
};

VideoPanelWithContexts.propTypes = {
  i18n: PropTypes.shape({
    getLocale: PropTypes.func.isRequired,
  }).isRequired,
  campaignId: PropTypes.number.isRequired,
};

const WrappedComponent = withRecommendationContext(VideoPanelWithContexts);

export { WrappedComponent as VideoPanel };

